import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useLiveAPIContext } from "../contexts/LiveAPIContext";
import type {
  SanitizedQuestion,
  ProxyRewards,
  ServerAnswerRevealed,
  ServerQuestion,
  ServerError,
  ServerGoAway,
  RewardRecognition,
} from "../../shared/live-proxy-protocol";
import type { ConnectionStatus } from "../lib/live-proxy-client";

// ── Session state ─────────────────────────────────────────────────────────────

export type QuizPhase = "idle" | "loading" | "ready" | "answered";

export type QuizSession = {
  phase: QuizPhase;
  question: SanitizedQuestion | null;
  answered: string | null;
  correctAnswer: string | null;
  hebrewExplanation: string | undefined;
  flashcardBack: string | undefined;
  flashcardExample: string | undefined;
  canAdvance: boolean;
  rewards: ProxyRewards | null;
  streak: number;
  recognition: RewardRecognition | null;
  previousOutcome: "correct" | "incorrect" | null;
  goAway: { timeLeft?: string } | null;
  error: string | null;
  /** Transient, non-fatal status shown while a question stays on screen (e.g. reconnecting) */
  notice: string | null;
  connectionStatus: ConnectionStatus;
  isAgentSpeaking: boolean;
};

export const QUIZ_SESSION_KEY = ["quiz-session"] as const;

export const INITIAL_SESSION: QuizSession = {
  phase: "idle",
  question: null,
  answered: null,
  correctAnswer: null,
  hebrewExplanation: undefined,
  flashcardBack: undefined,
  flashcardExample: undefined,
  canAdvance: false,
  rewards: null,
  streak: 0,
  recognition: null,
  previousOutcome: null,
  goAway: null,
  error: null,
  notice: null,
  connectionStatus: "idle",
  isAgentSpeaking: false,
};

// ── Hook ──────────────────────────────────────────────────────────────────────

type UseQuizSessionOptions = {
  onAdvanced?: (newStep: number) => void;
};

export function useQuizSession({ onAdvanced }: UseQuizSessionOptions = {}) {
  const { client, status } = useLiveAPIContext();
  const queryClient = useQueryClient();

  const onAdvancedRef = useRef(onAdvanced);
  onAdvancedRef.current = onAdvanced;

  // ── Read session from TanStack Query cache ──
  // queryFn returns INITIAL_SESSION only on first call; all subsequent state
  // comes from queryClient.setQueryData calls triggered by WS events.
  const { data: session = INITIAL_SESSION } = useQuery<QuizSession>({
    queryKey: QUIZ_SESSION_KEY,
    queryFn: () => INITIAL_SESSION,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // ── Keep connectionStatus in sync ──
  useEffect(() => {
    queryClient.setQueryData<QuizSession>(QUIZ_SESSION_KEY, (prev = INITIAL_SESSION) => ({
      ...prev,
      connectionStatus: status,
    }));
  }, [status, queryClient]);

  // ── Updater helper (always uses latest cache via updater form) ──
  const update = useCallback(
    (updater: (prev: QuizSession) => QuizSession) => {
      queryClient.setQueryData<QuizSession>(
        QUIZ_SESSION_KEY,
        (prev = INITIAL_SESSION) => updater(prev),
      );
    },
    [queryClient],
  );

  // ── WebSocket events → cache ──
  useEffect(() => {
    const onQuestion = (data: ServerQuestion) => {
      if (data.advanced && data.currentStep) {
        onAdvancedRef.current?.(data.currentStep);
      }
      update((prev) => ({
        ...prev,
        phase: "loading",
        question: data.question,
        answered: null,
        correctAnswer: null,
        hebrewExplanation: undefined,
        flashcardBack: undefined,
        flashcardExample: undefined,
        canAdvance: false,
        error: null,
        notice: null,
        goAway: null,
        recognition: null,
        rewards: data.rewards ?? prev.rewards,
      }));
    };

    const onSetupComplete = () => {
      update((prev) => ({ ...prev, phase: "ready", notice: null }));
    };

    const onAnswerRevealed = (data: ServerAnswerRevealed) => {
      update((prev) => ({
        ...prev,
        phase: "answered",
        answered: data.selectedOption || "flip",
        correctAnswer: data.correctAnswer,
        hebrewExplanation: data.hebrewExplanation,
        flashcardBack: data.hebrewBack,
        flashcardExample: data.exampleSentence,
        rewards: data.rewards ?? prev.rewards,
        streak: data.streak,
        recognition: data.recognition,
        previousOutcome: data.isCorrect ? "correct" : "incorrect",
      }));
    };

    const onAgentAudio = () =>
      update((prev) => (prev.isAgentSpeaking ? prev : { ...prev, isAgentSpeaking: true }));

    const onTurnComplete = () => {
      update((prev) => ({
        ...prev,
        isAgentSpeaking: false,
        ...(prev.phase === "answered" ? { canAdvance: true } : {}),
      }));
    };

    const onInterrupted = () =>
      update((prev) => (prev.isAgentSpeaking ? { ...prev, isAgentSpeaking: false } : prev));

    const onGoAway = (data: ServerGoAway) => {
      update((prev) => ({ ...prev, goAway: { timeLeft: data.timeLeft } }));
    };

    const onError = (data: ServerError) => {
      if (data.fatal) {
        Sentry.captureMessage(`Live proxy fatal error: ${data.message}`, {
          level: "error",
          tags: { flow: "live-proxy", event: "fatal-error" },
        });
        update((prev) => ({ ...prev, error: data.message, phase: "idle", question: null }));
        return;
      }
      // Non-fatal error with no question on screen: without surfacing it the
      // child is stuck on the loading dots forever.
      update((prev) => {
        if (prev.phase === "loading") {
          Sentry.captureMessage(`Live proxy error while loading question: ${data.message}`, {
            level: "error",
            tags: { flow: "live-proxy", event: "question-load-error" },
          });
          return { ...prev, error: "משהו השתבש, נסו שוב", phase: "idle", question: null };
        }
        // Mid-question (e.g. "מתחבר מחדש..."): show a transient notice instead
        // of swallowing it — the child otherwise sees a quiz that ignores them.
        if (prev.phase === "ready" || prev.phase === "answered") {
          Sentry.addBreadcrumb({
            category: "live-proxy",
            message: `Non-fatal proxy error mid-question: ${data.message}`,
            level: "warning",
          });
          return { ...prev, notice: data.message };
        }
        return prev;
      });
    };

    const onStatus = (s: ConnectionStatus) => {
      if (s === "disconnected") {
        Sentry.captureMessage("Live proxy WS: max reconnects reached", {
          level: "error",
          tags: { flow: "live-proxy", event: "reconnect-failed" },
        });
        update((prev) => ({ ...prev, error: "החיבור נותק", phase: "idle", question: null }));
      }
    };

    client.on("audio",          onAgentAudio);
    client.on("question",       onQuestion);
    client.on("setup_complete", onSetupComplete);
    client.on("answer_revealed", onAnswerRevealed);
    client.on("turn_complete",  onTurnComplete);
    client.on("interrupted",    onInterrupted);
    client.on("go_away",        onGoAway);
    client.on("error",          onError);
    client.on("status",         onStatus);

    return () => {
      client.off("audio",          onAgentAudio);
      client.off("question",       onQuestion);
      client.off("setup_complete", onSetupComplete);
      client.off("answer_revealed", onAnswerRevealed);
      client.off("turn_complete",  onTurnComplete);
      client.off("interrupted",    onInterrupted);
      client.off("go_away",        onGoAway);
      client.off("error",          onError);
      client.off("status",         onStatus);
      // A new visit starts at the entry view; earned progress stays in the header.
      update((prev) => ({
        ...INITIAL_SESSION,
        rewards: prev.rewards,
        streak: prev.streak,
        previousOutcome: prev.previousOutcome,
      }));
    };
  }, [client, update]);

  // Auto-clear the transient notice so it can't outlive the hiccup it reports
  useEffect(() => {
    if (!session.notice) return;
    const t = setTimeout(() => {
      update((prev) => (prev.notice ? { ...prev, notice: null } : prev));
    }, 5_000);
    return () => clearTimeout(t);
  }, [session.notice, update]);

  // Safety net: enable "Next" after 30s if turn_complete never fires
  useEffect(() => {
    if (session.phase !== "answered") return;
    const t = setTimeout(() => {
      update((prev) => (prev.phase === "answered" ? { ...prev, canAdvance: true, isAgentSpeaking: false } : prev));
    }, 30_000);
    return () => clearTimeout(t);
  }, [session.phase, update]);

  // ── Actions ──

  const startQuestion = useCallback(
    (personaProfileId: string) => {
      const current = queryClient.getQueryData<QuizSession>(QUIZ_SESSION_KEY) ?? INITIAL_SESSION;
      update(() => ({
        ...INITIAL_SESSION,
        phase: "loading",
        connectionStatus: current.connectionStatus,
        // Carry rewards forward so the topbar doesn't flicker to zero
        rewards: current.rewards,
        streak: current.streak,
        previousOutcome: current.previousOutcome,
      }));
      client.startQuestion(personaProfileId, current.previousOutcome);
    },
    [client, queryClient, update],
  );

  const sendChip = useCallback(
    (chip: "hint" | "repeat") => {
      client.sendChip(chip);
    },
    [client],
  );

  const sendAnswerChoice = useCallback(
    (text: string) => {
      client.sendAnswerChoice(text);
    },
    [client],
  );

  return { session, startQuestion, sendChip, sendAnswerChoice };
}
